import { isDef } from "@redotech/util/checks";
import { assertNever } from "@redotech/util/type";
import { ShopifyPlan } from "../integration/shopify/shopify-plan";
import {
  ADVANCED_CHECKOUT_CONVERSION_METRICS_EARLIEST_AVAILABLE_DATE,
  CheckoutAnalyticsVersion,
  getCheckoutAnalyticsVersionForSplit,
  isAdvancedCheckoutAnalyticsVersion,
} from "./checkout-conversion-metrics/analytics";

/**
 * Currently, whether advanced checkout features are available is determined by
 * whether the team has Shopify Plus. This check is thin right now, but it
 * exists to centralize the logic for checking if advanced checkout features
 * are available (to easily track all the features that are conditioned on it).
 */
export function isAdvancedCheckoutFeaturesAvailable({
  hasShopifyPlus,
  planDisplayName,
  teamId,
}: {
  hasShopifyPlus: boolean;
  planDisplayName: string | undefined;
  teamId: string;
}): boolean {
  return (
    hasShopifyPlus ||
    planDisplayName === "Plus Trial" ||
    !!(teamId && SHOP_PLUS_OVERRIDE_TEAM_IDS.includes(teamId))
  );
}

const SHOP_PLUS_OVERRIDE_TEAM_IDS = [
  // Dev teams
  "67e58c8b02978c77ac941989", // [Dev] Redo | Coleman 2
  "67d334c95d63bb9f8f7aa3ac", // [Dev] Redo | Ben Carper 2
];

export function isCheckoutMetricsInsightsAvailable({
  hasShopifyPlus,
  planDisplayName,
  teamId,
}: {
  hasShopifyPlus: boolean;
  planDisplayName: string | undefined;
  teamId: string;
}): boolean {
  return isAdvancedCheckoutFeaturesAvailable({
    hasShopifyPlus,
    planDisplayName,
    teamId,
  });
}

export function shouldShowSegmentedCheckoutMetricsTable({
  hasShopifyPlus,
  planDisplayName,
  splitStartDate,
  splitAnalyticsVersionOverride,
  teamId,
}: {
  hasShopifyPlus: boolean;
  planDisplayName: string | undefined;
  splitStartDate: Date | undefined;
  splitAnalyticsVersionOverride: CheckoutAnalyticsVersion | undefined;
  teamId: string;
}): boolean {
  const _isAdvancedCheckoutFeaturesAvailable =
    isAdvancedCheckoutFeaturesAvailable({
      hasShopifyPlus,
      planDisplayName,
      teamId,
    });
  const analyticsVersion = getCheckoutAnalyticsVersionForSplit({
    shouldUseAdvanced: _isAdvancedCheckoutFeaturesAvailable,
    splitStartDate,
    splitAnalyticsVersionOverride,
  });
  const _isAdvancedCheckoutAnalyticsVersion =
    isAdvancedCheckoutAnalyticsVersion(analyticsVersion);
  const hasStarted = isDef(splitStartDate);
  const startDateIsAfterMetricsAvailableDate =
    splitStartDate &&
    splitStartDate.getTime() >
      ADVANCED_CHECKOUT_CONVERSION_METRICS_EARLIEST_AVAILABLE_DATE.getTime();
  return (
    _isAdvancedCheckoutAnalyticsVersion &&
    hasStarted &&
    !!startDateIsAfterMetricsAvailableDate
  );
}

export function shouldShowCustomVisualsForSplit({
  hasShopifyPlus,
  planDisplayName,
  teamId,
  splitStartDate,
  splitAnalyticsVersionOverride,
}: {
  hasShopifyPlus: boolean;
  planDisplayName: string | undefined;
  teamId: string;
  splitStartDate: Date | undefined;
  splitAnalyticsVersionOverride: CheckoutAnalyticsVersion | undefined;
}): boolean {
  const _isAdvancedCheckoutFeaturesAvailable =
    isAdvancedCheckoutFeaturesAvailable({
      hasShopifyPlus,
      planDisplayName,
      teamId,
    });
  const analyticsVersion = getCheckoutAnalyticsVersionForSplit({
    shouldUseAdvanced: _isAdvancedCheckoutFeaturesAvailable,
    splitStartDate,
    splitAnalyticsVersionOverride,
  });
  switch (analyticsVersion) {
    case "advanced-v09-16-2025":
      // Custom visuals rely on the existence of the advanced checkout delivery
      // options metrics existing. This is the first version with those metrics
      // available.
      return true;
    case "advanced-v08-13-2025":
    case "basic":
      return false;
    default:
      return assertNever(analyticsVersion);
  }
}

export function isCheckoutWidgetsAvailable({
  shopifyPlan,
  planDisplayName,
  teamId,
}: {
  shopifyPlan: ShopifyPlan;
  planDisplayName: string | undefined;
  teamId: string;
}): boolean {
  return isAdvancedCheckoutFeaturesAvailable({
    hasShopifyPlus: shopifyPlan === ShopifyPlan.PLUS,
    planDisplayName,
    teamId,
  });
}
